import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { INSURANCE_TYPES } from '../../../../../../../constants';
import { onboardingQuestionnaireActions } from '../../../../../../../store';
import { getAllFieldsTouched } from '../../../../../../common';
import {
  applyInsuranceFieldOverrides,
  getChangedSavedFieldValues,
  getInsurancePayload,
  getSavedFieldValues,
  getValuesWithFieldOverride,
  isInsuranceDateFieldValid,
  isInsuranceFieldSchemaValid,
} from './utils';

import type { FormikProps } from 'formik';
import type { AppDispatch } from '../../../../../../../store';
import type { InsuranceType } from '../../../../../../../utils/types';
import type {
  InsuranceFieldName,
  InsuranceFieldOverride,
  InsuranceFieldValue,
  InsuranceSavedFieldValues,
} from './types';
import type { InsuranceFormValues } from '../../../../types';

export default function useSaveInsuranceOnBlur(
  formik: FormikProps<InsuranceFormValues>,
  initialValues: InsuranceFormValues,
  insuranceType: InsuranceType,
) {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { onboardingQuestionnaireId } = useParams<'onboardingQuestionnaireId'>();
  const savedFieldValuesRef = useRef<InsuranceSavedFieldValues>(getSavedFieldValues(initialValues));
  const savingPromiseRef = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    savedFieldValuesRef.current = getSavedFieldValues(initialValues);
  }, [initialValues]);

  const performInsuranceSave = useCallback(
    async (values: InsuranceFormValues) => {
      if (!onboardingQuestionnaireId) {
        return;
      }

      const previousSave = savingPromiseRef.current;
      const currentSave = (async () => {
        try {
          await previousSave;
        } catch {
          // Swallow prior save errors so this save still proceeds.
        }

        const changedSavedValues = getChangedSavedFieldValues(values, savedFieldValuesRef.current);

        if (Object.keys(changedSavedValues).length === 0) {
          return;
        }

        const upsertAction =
          insuranceType === INSURANCE_TYPES.PRIMARY
            ? onboardingQuestionnaireActions.upsertOnboardingQuestionnairePrimaryInsurance
            : onboardingQuestionnaireActions.upsertOnboardingQuestionnaireSecondaryInsurance;

        const resultAction = await dispatch(
          upsertAction({
            onboardingQuestionnaireId,
            data: getInsurancePayload(values),
            getAccessTokenSilently,
          }),
        );

        if (!upsertAction.fulfilled.match(resultAction)) {
          return;
        }

        const errors = await formik.validateForm(values);
        formik.setErrors(errors);
        formik.setTouched(getAllFieldsTouched(values), false);
        savedFieldValuesRef.current = {
          ...savedFieldValuesRef.current,
          ...changedSavedValues,
        };
      })();

      savingPromiseRef.current = currentSave;
      await currentSave;
    },
    [dispatch, formik, getAccessTokenSilently, insuranceType, onboardingQuestionnaireId],
  );

  const saveFieldOnBlur = useCallback(
    async (name: InsuranceFieldName, value?: InsuranceFieldValue) => {
      const values = getValuesWithFieldOverride(formik.values, name, value);

      if (!isInsuranceDateFieldValid(name, values)) {
        return;
      }

      if (!(await isInsuranceFieldSchemaValid(name, values))) {
        return;
      }

      await performInsuranceSave(values);
    },
    [formik, performInsuranceSave],
  );

  const saveInsuranceFields = useCallback(
    async (overrides: InsuranceFieldOverride[]) => {
      const values = applyInsuranceFieldOverrides(formik.values, overrides);

      for (const { name } of overrides) {
        if (!isInsuranceDateFieldValid(name, values)) {
          return;
        }

        if (!(await isInsuranceFieldSchemaValid(name, values))) {
          return;
        }
      }

      await performInsuranceSave(values);
    },
    [formik, performInsuranceSave],
  );

  return useMemo(
    () => ({ saveFieldOnBlur, saveInsuranceFields }),
    [saveFieldOnBlur, saveInsuranceFields],
  );
}
